import type { CSSProperties } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import {
  Plane,
  Train,
  MapPin,
  MessageSquare,
  Plus,
} from 'lucide-react';

export type CustomerDetailsData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;

  bookingForSomeoneElse: boolean;
  otherPersonName: string;
  otherPersonPhone: string;

  arrivalType: 'flight' | 'train' | 'text';
  airline: string;
  flightNumber: string;
  arrivalTerminal: string;
  arrivalTime: string;
  locationValue?: string;
  options: string[];
  specialRequirements: string;
};

type Step2Props = {
  data: CustomerDetailsData;
  updateData: (data: Partial<CustomerDetailsData>) => void;
};

const moreOptionsList = [
  { id: 'infant-seat', label: 'Infant Seat (0–1 year) €5' },
  { id: 'child-seat', label: 'Child Seat (1–3 year) €5' },
  { id: 'booster-seat', label: 'Booster Seat (3–12 year)' },
  { id: 'ski-snowboard', label: 'Skis / Snowboard' },
];

const airlineOptions = [
  'Austrian Airlines',
  'Lufthansa',
  'Emirates',
  'Others',
];

const trainOperatorOptions = [
  'Westbahn',
  'Others',
];

const isGoogleMapsUrl = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed) {
    return false;
  }

  try {
    const url = new URL(trimmed);
    const hostname = url.hostname.toLowerCase();

    return (
      hostname === 'maps.app.goo.gl' ||
      hostname === 'goo.gl' ||
      hostname.includes('google.com') ||
      hostname.includes('google.')
    );
  } catch {
    return false;
  }
};

export const Step2CustomerDetails = ({
  data,
  updateData,
}: Step2Props) => {
  const toggleOption = (optionId: string) => {
    updateData({
      options: data.options.includes(optionId)
        ? data.options.filter((id) => id !== optionId)
        : [...data.options, optionId],
    });
  };

  const locationValue = data.locationValue?.trim() || '';

  const locationIsGoogleMaps =
    isGoogleMapsUrl(locationValue);

  return (
    <div className="flex flex-col gap-6 w-full max-w-[1000px] mx-auto px-[32px]">
      <div>
        <h2 className="font-host text-[18px] font-bold text-[#1A1A1A]">
          Customer Details
        </h2>

        <p className="font-host text-[13px] text-[#757575] mt-1">
          Enter the customer's contact information for this booking.
        </p>
      </div>

      {/* Basic Contact Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            First Name{' '}
            <span className="text-[#FF6A1A]">*</span>
          </label>

          <input
            type="text"
            placeholder="Enter first name"
            value={data.firstName}
            onChange={(e) =>
              updateData({
                firstName: e.target.value,
              })
            }
            className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Last Name{' '}
            <span className="text-[#FF6A1A]">*</span>
          </label>

          <input
            type="text"
            placeholder="Enter last name"
            value={data.lastName}
            onChange={(e) =>
              updateData({
                lastName: e.target.value,
              })
            }
            className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Email Address{' '}
            <span className="text-[#FF6A1A]">*</span>
          </label>

          <input
            type="email"
            placeholder="Enter email address"
            value={data.email}
            onChange={(e) =>
              updateData({
                email: e.target.value,
              })
            }
            className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="font-host text-[13px] font-medium text-[#757575]">
            Phone Number{' '}
            <span className="text-[#FF6A1A]">*</span>
          </label>

          <div className="min-h-[48px] overflow-visible rounded-[8px] bg-white border border-[#E0E0E0] focus-within:border-[#FF6A1A]">
            <PhoneInput
              defaultCountry="at"
              preferredCountries={[
                'at',
                'in',
                'gb',
                'us',
              ]}
              value={data.phoneNumber}
              onChange={(phone) =>
                updateData({
                  phoneNumber: phone,
                })
              }
              forceDialCode
              placeholder="Phone Number"
              className="w-full"
              style={
                {
                  '--react-international-phone-height':
                    '46px',
                  '--react-international-phone-background-color':
                    'transparent',
                  '--react-international-phone-text-color':
                    '#000000',
                  '--react-international-phone-font-size':
                    '14px',
                  '--react-international-phone-border-color':
                    'transparent',
                  '--react-international-phone-border-radius':
                    '8px',
                } as CSSProperties
              }
              inputClassName="!w-full !border-0 !bg-transparent !px-4 !font-host !text-[14px] !font-medium !text-black !outline-none"
              countrySelectorStyleProps={{
                buttonClassName:
                  '!h-[46px] !min-w-[80px] !rounded-l-[8px] !border-0 !border-r !border-[#E0E0E0] !bg-[#F5F5F5] !px-4',
                buttonContentWrapperClassName:
                  '!gap-2 !font-host !text-[14px] !font-medium',
                flagClassName:
                  '!h-[22px] !w-[22px]',
                dropdownStyleProps: {
                  className:
                    '!z-[200] !max-h-[300px] !w-[min(320px,calc(100vw-2rem))] !rounded-[12px] !border !border-[#D8D8D8] !bg-white !shadow-[0_10px_30px_rgba(0,0,0,0.16)]',
                },
              }}
              inputProps={{
                name: 'phoneNumber',
                autoComplete: 'tel',
                'aria-label': 'Phone number',
              }}
            />
          </div>
        </div>
      </div>

      {/* Arrival / Location Information */}
      <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-[24px]">
        <h3 className="font-host text-[14px] font-medium text-[#757575] mb-4">
          Arrival Information
        </h3>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          {/* Flight */}
          <button
            type="button"
            onClick={() =>
              updateData({
                arrivalType: 'flight',
                airline: '',
                flightNumber: '',
                arrivalTerminal: '',
                arrivalTime: '',
                locationValue: '',
              })
            }
            className={`flex items-center justify-center gap-2 h-[40px] px-6 rounded-[8px] font-host text-[14px] font-bold transition-colors ${
              data.arrivalType === 'flight'
                ? 'bg-[#3B5998] text-white'
                : 'bg-white border border-[#3B5998] text-[#3B5998] hover:bg-[#F5F5F5]'
            }`}
          >
            <Plane
              size={16}
              className={
                data.arrivalType === 'flight'
                  ? 'text-white'
                  : 'text-[#3B5998]'
              }
            />
            Flight
          </button>

          {/* Train */}
          <button
            type="button"
            onClick={() =>
              updateData({
                arrivalType: 'train',
                airline: '',
                flightNumber: '',
                arrivalTerminal: '',
                arrivalTime: '',
                locationValue: '',
              })
            }
            className={`flex items-center justify-center gap-2 h-[40px] px-6 rounded-[8px] font-host text-[14px] font-bold transition-colors ${
              data.arrivalType === 'train'
                ? 'bg-[#3B5998] text-white'
                : 'bg-white border border-[#3B5998] text-[#3B5998] hover:bg-[#F5F5F5]'
            }`}
          >
            <Train
              size={16}
              className={
                data.arrivalType === 'train'
                  ? 'text-white'
                  : 'text-[#3B5998]'
              }
            />
            Train
          </button>

          {/* Location */}
          <button
            type="button"
            onClick={() =>
              updateData({
                arrivalType: 'text',
                airline: '',
                flightNumber: '',
                arrivalTerminal: '',
                arrivalTime: '',
                locationValue: '',
              })
            }
            className={`flex items-center justify-center gap-2 h-[40px] px-6 rounded-[8px] font-host text-[14px] font-bold transition-colors ${
              data.arrivalType === 'text'
                ? 'bg-[#3B5998] text-white'
                : 'bg-white border border-[#3B5998] text-[#3B5998] hover:bg-[#F5F5F5]'
            }`}
          >
            <MapPin
              size={16}
              className={
                data.arrivalType === 'text'
                  ? 'text-white'
                  : 'text-[#3B5998]'
              }
            />
            Location
          </button>
        </div>

        {/* Flight / Train Fields */}
        {(data.arrivalType === 'flight' ||
          data.arrivalType === 'train') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Airline / Train Operator */}
            <label className="flex flex-col h-[64px] rounded-[8px] bg-[#F5F5F5] px-4 py-2 relative">
              <span className="block font-host text-[11px] font-medium text-[#9E9E9E]">
                {data.arrivalType === 'flight'
                  ? 'Airline'
                  : 'Train Operator'}
              </span>

              <select
                value={data.airline}
                onChange={(e) =>
                  updateData({
                    airline: e.target.value,
                  })
                }
                className="w-full h-full bg-transparent font-host text-[14px] font-medium text-[#1A1A1A] outline-none appearance-none cursor-pointer mt-1"
              >
                <option value="">
                  {data.arrivalType === 'flight'
                    ? 'Search Airline'
                    : 'Select Train Operator'}
                </option>

                {(data.arrivalType === 'flight'
                  ? airlineOptions
                  : trainOperatorOptions
                ).map((option) => (
                  <option
                    key={option}
                    value={option}
                  >
                    {option}
                  </option>
                ))}
              </select>

              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="#1A1A1A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </label>

            {/* Flight / Train Number */}
            <label className="flex flex-col h-[64px] rounded-[8px] bg-[#F5F5F5] px-4 py-2">
              <span className="block font-host text-[11px] font-medium text-[#9E9E9E]">
                {data.arrivalType === 'flight'
                  ? 'Flight Number'
                  : 'Train Number'}
              </span>

              <input
                type="text"
                value={data.flightNumber}
                onChange={(e) =>
                  updateData({
                    flightNumber: e.target.value,
                  })
                }
                placeholder={
                  data.arrivalType === 'flight'
                    ? 'ex: OS452'
                    : 'ex: RJ123'
                }
                className="w-full h-full bg-transparent font-host text-[14px] font-medium text-[#1A1A1A] outline-none mt-1"
              />
            </label>

            {/* Terminal / Platform */}
            <label className="flex flex-col h-[64px] rounded-[8px] bg-[#F5F5F5] px-4 py-2 relative">
              <span className="block font-host text-[11px] font-medium text-[#9E9E9E]">
                {data.arrivalType === 'flight'
                  ? 'Arrival Terminal'
                  : 'Arrival Platform / Location'}
              </span>

              <select
                value={data.arrivalTerminal}
                onChange={(e) =>
                  updateData({
                    arrivalTerminal: e.target.value,
                  })
                }
                className="w-full h-full bg-transparent font-host text-[14px] font-medium text-[#1A1A1A] outline-none appearance-none cursor-pointer mt-1"
              >
                {data.arrivalType === 'flight' ? (
                  <>
                    <option value="Terminal 1">
                      Terminal 1
                    </option>
                    <option value="Terminal 2">
                      Terminal 2
                    </option>
                    <option value="Terminal 3">
                      Terminal 3
                    </option>
                  </>
                ) : (
                  <>
                    <option value="Platform 1">
                      Platform 1
                    </option>
                    <option value="Platform 2">
                      Platform 2
                    </option>
                    <option value="Platform 3">
                      Platform 3
                    </option>
                    <option value="Platform 4">
                      Platform 4
                    </option>
                    <option value="Platform 5">
                      Platform 5
                    </option>
                  </>
                )}

                {data.arrivalTerminal &&
                  !(
                    data.arrivalType === 'flight'
                      ? [
                          'Terminal 1',
                          'Terminal 2',
                          'Terminal 3',
                        ].includes(
                          data.arrivalTerminal
                        )
                      : [
                          'Platform 1',
                          'Platform 2',
                          'Platform 3',
                          'Platform 4',
                          'Platform 5',
                        ].includes(
                          data.arrivalTerminal
                        )
                  ) && (
                    <option
                      value={data.arrivalTerminal}
                    >
                      {data.arrivalTerminal}
                    </option>
                  )}
              </select>

              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="#1A1A1A"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </label>

            {/* Arrival Time */}
            <label className="flex flex-col h-[64px] rounded-[8px] bg-[#F5F5F5] px-4 py-2">
              <span className="block font-host text-[11px] font-medium text-[#9E9E9E]">
                Scheduled Arrival Time
              </span>

              <input
                type="time"
                value={data.arrivalTime}
                onChange={(e) =>
                  updateData({
                    arrivalTime: e.target.value,
                  })
                }
                className="w-full h-full bg-transparent font-host text-[14px] font-medium text-[#1A1A1A] outline-none mt-1"
              />
            </label>
          </div>
        )}

        {/* Location Field */}
        {data.arrivalType === 'text' && (
          <div className="grid grid-cols-1 gap-4">
            <div className="flex flex-col min-h-[64px] rounded-[8px] bg-[#F5F5F5] px-4 py-2">
              <span className="block font-host text-[11px] font-medium text-[#9E9E9E]">
                Location
              </span>

              <div className="flex items-center gap-3 mt-1">
                <input
                  type="text"
                  value={data.locationValue || ''}
                  onChange={(e) =>
                    updateData({
                      locationValue:
                        e.target.value,
                    })
                  }
                  placeholder="Enter location or Google Maps link"
                  className="flex-1 min-w-0 h-[32px] bg-transparent font-host text-[14px] font-medium text-[#1A1A1A] placeholder:text-[#9E9E9E] outline-none"
                />

                {locationIsGoogleMaps && (
                  <a
                    href={locationValue}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 font-host text-[13px] font-medium text-[#3B5998] hover:underline"
                  >
                    Open
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* More Options & Special Requirements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* More Options */}
        <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-[24px]">
          <h3 className="font-host text-[16px] font-bold text-[#1A1A1A] mb-5">
            More Options
          </h3>

          <div className="flex flex-col gap-4">
            {moreOptionsList.map((option) => (
              <label
                key={option.id}
                className="flex items-center gap-3 cursor-pointer group"
              >
                <div className="relative flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={data.options.includes(
                      option.id
                    )}
                    onChange={() =>
                      toggleOption(option.id)
                    }
                    className="peer appearance-none w-5 h-5 rounded-[4px] border border-[#C2C2C2] checked:border-[#1A1A1A] checked:bg-[#1A1A1A] transition-colors cursor-pointer"
                  />

                  <svg
                    className="absolute pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
                    width="12"
                    height="10"
                    viewBox="0 0 12 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 5L4.5 8L11 1"
                      stroke="white"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <span className="font-host text-[14px] text-[#1A1A1A] group-hover:text-black transition-colors">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Special Requirements */}
        <div className="w-full rounded-[16px] border border-[#E0E0E0] bg-white p-[24px]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="flex items-center gap-2 font-host text-[14px] font-bold text-[#1A1A1A]">
              <MessageSquare
                size={16}
                className="text-[#1A1A1A] fill-[#1A1A1A]"
              />
              Enter special requirements
            </h3>

            <button
              type="button"
              className="text-[#1A1A1A]"
            >
              <Plus size={20} />
            </button>
          </div>

          <textarea
            value={data.specialRequirements}
            onChange={(e) =>
              updateData({
                specialRequirements:
                  e.target.value,
              })
            }
            className="w-full h-[140px] resize-none rounded-[8px] bg-[#F5F5F5] p-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] outline-none"
          />
        </div>
      </div>
    </div>
  );
};
